import { computed, reactive, ref } from 'vue';
import {
  cancelBatch as cancelBatchRequest,
  cancelLegacyBatch,
  completeLegacyBatch,
  createBatch,
  dismissBatch as dismissBatchRequest,
  getBatch,
  getBatchItems,
  getBulkCvSettings,
  getCollection,
  getLegacyItems,
  isBatchApiUnavailable,
  listBatches,
  resolveBatchItem,
  resolveBatchItems,
  resolveLegacyItem,
  startBatch,
  uploadBatchFiles
} from './bulkCvApi';
import { BULK_CV_FILE_POLICY } from './bulkCvApi';

export const BULK_CV_ACTIONS = Object.freeze({
  OVERRIDE: 'override_existing_candidate',
  DUPLICATE: 'create_duplicate_candidate',
  USE_EXISTING: 'use_existing_candidate',
  UPDATE_APPLICANT: 'update_existing_applicant',
  KEEP_APPLICANT: 'keep_existing_applicant',
  SKIP: 'skip'
});

export const BULK_CV_STATUS_META = Object.freeze({
  waiting_for_upload: {
    label: 'Waiting for upload',
    variant: 'secondary',
    icon: 'clock'
  },
  uploading: { label: 'Uploading', variant: 'info', icon: 'upload-cloud' },
  uploaded: { label: 'Uploaded', variant: 'info', icon: 'check-circle' },
  // Queued is an internal backend state while the intentional parser delay is
  // running. Present it as active processing so users do not see an artificial
  // waiting step.
  queued: { label: 'Processing', variant: 'info', icon: 'loader' },
  parsing: { label: 'Parsing CV', variant: 'info', icon: 'loader' },
  candidate_match_check: {
    label: 'Checking candidate matches',
    variant: 'info',
    icon: 'search'
  },
  action_required: {
    label: 'Action required',
    variant: 'warning',
    icon: 'alert-circle'
  },
  creating_candidate: {
    label: 'Creating candidate',
    variant: 'info',
    icon: 'user-plus'
  },
  updating_candidate: {
    label: 'Updating candidate',
    variant: 'info',
    icon: 'user-check'
  },
  creating_applicant: {
    label: 'Creating applicant',
    variant: 'info',
    icon: 'briefcase'
  },
  updating_existing_applicant: {
    label: 'Updating existing applicant',
    variant: 'info',
    icon: 'refresh-cw'
  },
  completed: { label: 'Completed', variant: 'success', icon: 'check' },
  skipped: { label: 'Skipped', variant: 'secondary', icon: 'minus-circle' },
  failed: { label: 'Failed', variant: 'danger', icon: 'x-circle' },
  cancelled: { label: 'Cancelled', variant: 'secondary', icon: 'slash' }
});

const TERMINAL_BATCH_STATUSES = [
  'completed',
  'completed_with_errors',
  'cancelled',
  'failed'
];
const ACTIVE_BATCH_STATUSES = [
  'draft',
  'uploading',
  'uploaded',
  'queued',
  'processing',
  'action_required'
];
const STARTED_BATCH_STATUSES = [
  'queued',
  'processing',
  'action_required',
  ...TERMINAL_BATCH_STATUSES
];
const TERMINAL_ITEM_STATUSES = ['completed', 'skipped', 'failed', 'cancelled'];
const SELECTED_BATCH_STORAGE_KEY = 'bulk-cv-selected-batch';
const LEGACY_BATCH_ID = 'legacy-current';

const cleanStoredBatchId = (value) => {
  const batchId = String(value ?? '').trim();
  if (
    !batchId ||
    ['null', 'undefined', 'false', '[object object]'].includes(
      batchId.toLowerCase()
    )
  ) {
    return null;
  }
  return batchId;
};

const batches = ref([]);
const selectedBatchId = ref(
  typeof window !== 'undefined'
    ? cleanStoredBatchId(
        window.localStorage.getItem(SELECTED_BATCH_STORAGE_KEY)
      )
    : null
);
// The backend is authoritative. This short-lived guard only prevents an older
// in-flight refresh from putting a just-dismissed batch back into the tray.
const dismissedBatchIds = ref([]);
const itemsByBatch = reactive({});
const loadingBatches = ref(false);
const loadingBatch = ref(false);
const creatingBatch = ref(false);
const batchApiMode = ref('unknown');
const requestError = ref('');
const actionLoading = reactive({});
const initialized = ref(false);
const pendingDraft = ref(null);
const uploadProgress = reactive({
  batchId: null,
  completedFiles: 0,
  totalFiles: 0,
  percent: 0,
  stage: null,
  error: ''
});
const uploadTransferLocked = computed(
  () =>
    creatingBatch.value &&
    ['prepare', 'upload'].includes(String(uploadProgress.stage || 'prepare'))
);
const policy = reactive({
  allow_duplicate_candidates: true,
  allow_duplicate_applicants_same_job: false,
  automatically_update_candidate: false,
  keep_cv_version_history: true,
  auto_add_existing_candidate: false,
  duplicate_matching_fields: [],
  max_file_size: BULK_CV_FILE_POLICY.maxSizeBytes,
  max_files: BULK_CV_FILE_POLICY.maxFiles,
  supported_extensions: BULK_CV_FILE_POLICY.extensions.slice(),
  parsing_delay_seconds: null
});
const permissions = reactive({});
const candidateOnlyPermissions = reactive({});
// The URL is issued by the backend for the authenticated user's reporting
// scope. Native WebSocket clients cannot attach this application's JWT header,
// so an arbitrary URL is never constructed in the browser.
const realtime = reactive({
  authorized: false,
  websocket_url: '',
  subprotocol: '',
  ticket_expires_in_seconds: 0,
  scope: 'own'
});
const reporting = reactive({
  scope: 'own',
  can_view_team: false
});
const refreshPromises = {
  active: null,
  history: null
};
const batchRefreshSequences = new Map();
let batchRefreshSequence = 0;
let visibleBatchRefreshes = 0;
let stateGeneration = 0;
let settingsPromise = null;

const wasDismissedThisSession = (batch) =>
  Boolean(batch?.id && dismissedBatchIds.value.includes(String(batch.id)));

const cleanStatus = (value) => {
  const status = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    pending: 'waiting_for_upload',
    waiting: 'waiting_for_upload',
    match_check: 'candidate_match_check',
    matching: 'candidate_match_check',
    needs_action: 'action_required',
    need_action: 'action_required',
    duplicate: 'action_required',
    success: 'completed',
    complete: 'completed',
    error: 'failed',
    invalid: 'failed',
    invalid_cv: 'failed',
    canceled: 'cancelled'
  };
  return aliases[status] || status || 'waiting_for_upload';
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let size = bytes / 1024;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[unitIndex]}`;
};

const normalizeAction = (value) => {
  const action = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    override: BULK_CV_ACTIONS.OVERRIDE,
    duplicate: BULK_CV_ACTIONS.DUPLICATE,
    create_duplicate: BULK_CV_ACTIONS.DUPLICATE,
    use_existing: BULK_CV_ACTIONS.USE_EXISTING,
    add_existing_candidate: BULK_CV_ACTIONS.USE_EXISTING,
    update_applicant: BULK_CV_ACTIONS.UPDATE_APPLICANT,
    keep_applicant: BULK_CV_ACTIONS.KEEP_APPLICANT,
    discard: BULK_CV_ACTIONS.SKIP
  };
  return aliases[action] || action;
};

const normalizeScenario = (value) => {
  const scenario = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  const aliases = {
    a: 'no_match',
    scenario_a: 'no_match',
    new_candidate: 'no_match',
    b: 'existing_candidate',
    scenario_b: 'existing_candidate',
    candidate_exists: 'existing_candidate',
    c: 'existing_applicant',
    scenario_c: 'existing_applicant',
    applicant_exists: 'existing_applicant',
    multiple_possible_matches: 'multiple_matches',
    possible_matches: 'multiple_matches'
  };
  return aliases[scenario] || scenario || null;
};

const normalizeCandidate = (value) => {
  if (!value) return null;
  if (['number', 'string'].includes(typeof value)) return { id: value };
  const candidate = value.candidate || value;
  const fallbackName = candidate.name || candidate.full_name || '';
  return {
    ...candidate,
    id: candidate.id || value.candidate_id || value.existing_candidate_id,
    first_name: candidate.first_name || fallbackName,
    middle_name: candidate.middle_name || '',
    last_name: candidate.last_name || '',
    candidate_email:
      candidate.candidate_email ||
      candidate.primary_email ||
      candidate.email ||
      '',
    candidate_phone:
      candidate.candidate_phone ||
      candidate.primary_phone ||
      candidate.phone ||
      '',
    current_company:
      candidate.current_company ||
      candidate.current_employer ||
      candidate.employer ||
      '',
    candidate_location: candidate.candidate_location || candidate.location || ''
  };
};

const normalizeApplicant = (value) => {
  if (!value) return null;
  if (['number', 'string'].includes(typeof value)) return { id: value };
  return {
    ...value,
    id: value.id || value.applicant_id,
    stage: value.stage || value.workflow_stage || null,
    status: value.status || null,
    detail_url_authoritative: Object.prototype.hasOwnProperty.call(
      value,
      'detail_url'
    )
  };
};

const normalizeEvidence = (value) => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];
  return Object.entries(value).map(([field, evidenceValue]) => ({
    field,
    value: evidenceValue
  }));
};

const defaultAllowedActions = (scenario, legacy = false) => {
  if (legacy) {
    return [
      BULK_CV_ACTIONS.OVERRIDE,
      BULK_CV_ACTIONS.DUPLICATE,
      BULK_CV_ACTIONS.SKIP
    ];
  }
  if (scenario === 'existing_applicant') {
    return [
      BULK_CV_ACTIONS.UPDATE_APPLICANT,
      BULK_CV_ACTIONS.KEEP_APPLICANT,
      BULK_CV_ACTIONS.DUPLICATE,
      BULK_CV_ACTIONS.SKIP
    ];
  }
  if (['existing_candidate', 'multiple_matches'].includes(scenario)) {
    return [
      BULK_CV_ACTIONS.OVERRIDE,
      BULK_CV_ACTIONS.USE_EXISTING,
      BULK_CV_ACTIONS.DUPLICATE,
      BULK_CV_ACTIONS.SKIP
    ];
  }
  return [];
};

export const normalizeBulkCvItem = (
  raw = {},
  legacy = false,
  batchReference = null
) => {
  let status = cleanStatus(raw.status);
  if (legacy) {
    if (raw.invalid_cv) status = 'failed';
    else if (raw.candidate) status = 'action_required';
    else if (raw.parsed) status = 'completed';
    else if (raw.uploaded) status = 'parsing';
    else status = 'uploading';
  }

  const scenario = normalizeScenario(
    raw.scenario ||
      raw.duplicate_scenario ||
      (legacy && raw.candidate ? 'existing_candidate' : null)
  );
  let rawMatches =
    raw.matches || raw.possible_matches || raw.candidate_matches || [];
  if (!Array.isArray(rawMatches)) rawMatches = [];
  if (!rawMatches.length && raw.existing_candidate) {
    rawMatches = [
      {
        candidate: raw.existing_candidate,
        applicant: raw.existing_applicant,
        is_existing_applicant: Boolean(raw.existing_applicant)
      }
    ];
  }

  const matches = rawMatches.map((match) => ({
    ...match,
    id: match.id || match.candidate_id || match.candidate?.id,
    candidate: normalizeCandidate(match.candidate || match),
    applicant: normalizeApplicant(match.applicant || match.existing_applicant),
    is_existing_applicant: Boolean(
      match.is_existing_applicant || match.applicant || match.existing_applicant
    ),
    match_type: match.match_type || match.type || '',
    confidence: match.confidence ?? match.score ?? null,
    evidence: normalizeEvidence(match.evidence || match.matched_fields),
    scenario: normalizeScenario(
      match.scenario ||
        (match.is_existing_applicant ||
        match.applicant ||
        match.existing_applicant
          ? 'existing_applicant'
          : 'existing_candidate')
    ),
    allowed_actions: (match.allowed_actions || []).map(normalizeAction),
    actions_authoritative: Array.isArray(match.allowed_actions)
  }));

  const actionsAuthoritative = Array.isArray(raw.allowed_actions);
  const allowedActions = (
    actionsAuthoritative
      ? raw.allowed_actions
      : legacy
      ? defaultAllowedActions(scenario, true)
      : []
  ).map(normalizeAction);
  const effectiveAllowedActions =
    !legacy && matches.length === 1 && matches[0].actions_authoritative
      ? matches[0].allowed_actions
      : allowedActions;
  const errorValue = raw.error || raw.error_detail || raw.error_message;
  const error =
    typeof errorValue === 'string'
      ? {
          code: raw.error_code || '',
          message: errorValue
        }
      : errorValue
      ? {
          code: errorValue.code || raw.error_code || '',
          message:
            errorValue.message || errorValue.detail || 'Processing failed.',
          details: errorValue.details || null
        }
      : null;

  const finalCandidate = normalizeCandidate(
    raw.final_candidate ||
      raw.final_candidate_id ||
      raw.final_candidate_id_snapshot
  );
  const finalApplicant = normalizeApplicant(
    raw.final_applicant ||
      raw.final_applicant_id ||
      raw.final_applicant_id_snapshot
  );
  const duplicateStatus = cleanStatus(
    raw.duplicate_status || (scenario ? scenario : 'no_match')
  );
  const conditionKey =
    raw.condition_key || (legacy ? 'legacy:exact_email_duplicate' : null);

  return {
    ...raw,
    id: raw.id || raw.item_id || raw.file_id,
    batch_id:
      raw.batch_reference ||
      raw.batch?.reference ||
      batchReference ||
      raw.batch_id ||
      raw.batch?.id ||
      (legacy ? LEGACY_BATCH_ID : null),
    client_file_id: raw.client_file_id || raw.file_id,
    name: raw.name || raw.file_name || raw.filename || 'Unnamed CV',
    size: raw.size || raw.file_size || formatBytes(raw.size_bytes),
    size_bytes: raw.size_bytes || null,
    status,
    scenario,
    duplicate_status: duplicateStatus,
    parsed_candidate: normalizeCandidate(
      raw.parsed_candidate || raw.candidate || raw.parsed_data
    ),
    matches,
    allowed_actions: [...new Set(effectiveAllowedActions)],
    actions_authoritative: actionsAuthoritative,
    error,
    attempts: Number(raw.attempts || raw.attempt_count || 0),
    version: raw.version ?? raw.lock_version ?? null,
    final_candidate: finalCandidate,
    final_applicant: finalApplicant,
    final_candidate_exists: Boolean(raw.final_candidate),
    final_applicant_exists: Boolean(raw.final_applicant),
    condition_key: conditionKey,
    is_legacy: legacy
  };
};

const deriveCounts = (items = []) => {
  const counts = {
    total: items.length,
    waiting_for_upload: 0,
    uploading: 0,
    uploaded: 0,
    queued: 0,
    parsing: 0,
    candidate_match_check: 0,
    action_required: 0,
    creating_candidate: 0,
    updating_candidate: 0,
    creating_applicant: 0,
    updating_existing_applicant: 0,
    completed: 0,
    skipped: 0,
    failed: 0,
    cancelled: 0
  };
  items.forEach((item) => {
    if (Object.prototype.hasOwnProperty.call(counts, item.status)) {
      counts[item.status] += 1;
    }
  });
  return counts;
};

export const normalizeBulkCvBatch = (raw = {}, items = [], legacy = false) => {
  const reference =
    raw.reference || raw.batch_reference || raw.uuid || raw.id || null;
  const batchItems = items.map((item) =>
    normalizeBulkCvItem(item, legacy, reference)
  );
  const rawCounts = raw.counts || raw.summary || raw.stats || {};
  const counts = { ...deriveCounts(batchItems), ...rawCounts };
  counts.total = Number(
    rawCounts.total ?? raw.total_files ?? raw.item_count ?? counts.total
  );
  const jobValue = raw.job || raw.selected_job;
  const jobSnapshot = raw.job_snapshot || {};
  const jobId =
    (jobValue && typeof jobValue === 'object' ? jobValue.id : jobValue) ||
    raw.job_id ||
    raw.job_id_snapshot ||
    jobSnapshot.id;
  const hasJob = Number(jobId) > 0;
  const job = hasJob
    ? {
        ...jobSnapshot,
        ...(jobValue && typeof jobValue === 'object' ? jobValue : {}),
        id: jobId,
        position:
          (jobValue && typeof jobValue === 'object'
            ? jobValue.position || jobValue.title
            : '') ||
          raw.job_title ||
          raw.position ||
          jobSnapshot.position ||
          jobSnapshot.title ||
          ''
      }
    : null;

  let status = cleanStatus(raw.status || (legacy ? 'processing' : 'draft'));
  if (legacy && batchItems.length) {
    if (batchItems.some((item) => item.status === 'action_required')) {
      status = 'action_required';
    } else if (
      batchItems.every((item) => TERMINAL_ITEM_STATUSES.includes(item.status))
    ) {
      status = batchItems.some((item) => item.status === 'failed')
        ? 'completed_with_errors'
        : 'completed';
    } else status = 'processing';
  }

  return {
    ...raw,
    id: reference || raw.batch_id || (legacy ? LEGACY_BATCH_ID : null),
    db_id:
      raw.id && String(raw.id) !== String(reference)
        ? raw.id
        : raw.db_id || raw.batch_id || null,
    reference:
      raw.reference || raw.batch_reference || raw.reference_number || '',
    name:
      raw.name ||
      raw.batch_name ||
      (legacy ? 'Legacy bulk CV parsing' : `Bulk CV batch ${raw.id || ''}`),
    status,
    job,
    candidate_only: raw.candidate_only === true || (!legacy && !hasJob),
    counts,
    created_at: raw.created_at || raw.created_on || null,
    updated_at: raw.updated_at || raw.updated_on || null,
    completed_at: raw.completed_at || null,
    allowed_actions: (raw.allowed_actions || []).map(normalizeAction),
    actions_authoritative: Array.isArray(raw.allowed_actions),
    is_legacy: legacy
  };
};

const setSelectedBatch = (batchId) => {
  selectedBatchId.value = cleanStoredBatchId(batchId);
  if (typeof window === 'undefined') return;
  if (selectedBatchId.value) {
    window.localStorage.setItem(
      SELECTED_BATCH_STORAGE_KEY,
      selectedBatchId.value
    );
  } else {
    window.localStorage.removeItem(SELECTED_BATCH_STORAGE_KEY);
  }
};

const numericVersion = (value) => {
  const version = Number(value);
  return Number.isFinite(version) ? version : null;
};

const isOlderVersion = (incoming, current) => {
  const incomingVersion = numericVersion(incoming?.version);
  const currentVersion = numericVersion(current?.version);
  return (
    incomingVersion !== null &&
    currentVersion !== null &&
    incomingVersion < currentVersion
  );
};

const upsertBatch = (batch) => {
  if (!batch?.id) return;
  if (wasDismissedThisSession(batch)) return;
  const index = batches.value.findIndex(
    (existing) => String(existing.id) === String(batch.id)
  );
  if (index === -1) batches.value = [batch, ...batches.value];
  else {
    if (isOlderVersion(batch, batches.value[index])) return;
    const next = batches.value.slice();
    next[index] = { ...next[index], ...batch };
    batches.value = next;
  }
};

const mergeItemSnapshot = (batchId, items) => {
  const existing = itemsByBatch[String(batchId)] || [];
  const existingById = new Map(
    existing.map((item) => [
      String(item.id || item.client_file_id || item.file_id),
      item
    ])
  );
  const incomingIds = new Set();
  const merged = items.map((item) => {
    const itemId = String(item.id || item.client_file_id || item.file_id);
    incomingIds.add(itemId);
    const cached = existingById.get(itemId);
    return cached && isOlderVersion(item, cached) ? cached : item;
  });

  // Items are never deleted during parsing. Preserve a newer locally observed
  // item if an older paginated snapshot did not contain it yet.
  existing.forEach((item) => {
    const itemId = String(item.id || item.client_file_id || item.file_id);
    if (!incomingIds.has(itemId)) merged.push(item);
  });
  return merged;
};

const setBatchItems = (batchId, items) => {
  const normalized = items.map((item) =>
    normalizeBulkCvItem(item, String(batchId) === LEGACY_BATCH_ID, batchId)
  );
  const merged = mergeItemSnapshot(batchId, normalized);
  itemsByBatch[String(batchId)] = merged;
  const batch = batches.value.find(
    (candidate) => String(candidate.id) === String(batchId)
  );
  if (batch) {
    upsertBatch(
      normalizeBulkCvBatch(batch, merged, String(batchId) === LEGACY_BATCH_ID)
    );
  }
};

const getErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string') return data;
  return (
    data?.message ||
    data?.detail ||
    data?.error?.message ||
    data?.error?.detail ||
    error?.message ||
    fallback
  );
};

const loadSettings = async ({ quiet = false } = {}) => {
  if (settingsPromise) return settingsPromise;
  const generation = stateGeneration;
  let requestPromise;
  requestPromise = (async () => {
    try {
      const response = await getBulkCvSettings();
      if (generation !== stateGeneration) return null;
      const serverPolicy =
        response?.policy || response?.settings || response || {};
      const serverPermissions = response?.permissions || {};
      const serverCandidateOnlyPermissions =
        response?.candidate_only_permissions || serverPermissions;
      const serverRealtime = response?.realtime || {};
      const serverReporting = response?.reporting || {};
      Object.keys(policy).forEach((key) => {
        if (serverPolicy[key] !== undefined && serverPolicy[key] !== null) {
          policy[key] = serverPolicy[key];
        }
      });
      Object.keys(permissions).forEach((key) => delete permissions[key]);
      if (Array.isArray(serverPermissions)) {
        serverPermissions.forEach((action) => {
          permissions[normalizeAction(action)] = true;
        });
      } else if (serverPermissions && typeof serverPermissions === 'object') {
        Object.assign(permissions, serverPermissions);
      }
      Object.keys(candidateOnlyPermissions).forEach(
        (key) => delete candidateOnlyPermissions[key]
      );
      if (Array.isArray(serverCandidateOnlyPermissions)) {
        serverCandidateOnlyPermissions.forEach((action) => {
          candidateOnlyPermissions[normalizeAction(action)] = true;
        });
      } else if (
        serverCandidateOnlyPermissions &&
        typeof serverCandidateOnlyPermissions === 'object'
      ) {
        Object.assign(candidateOnlyPermissions, serverCandidateOnlyPermissions);
      }
      Object.assign(realtime, {
        authorized: serverRealtime.authorized === true,
        websocket_url: serverRealtime.websocket_url || serverRealtime.url || '',
        subprotocol: serverRealtime.subprotocol || '',
        ticket_expires_in_seconds: Number(
          serverRealtime.ticket_expires_in_seconds || 0
        ),
        scope: serverRealtime.scope || serverReporting.scope || 'own'
      });
      Object.assign(reporting, {
        scope:
          serverReporting.scope ||
          serverRealtime.scope ||
          response?.history_scope ||
          'own',
        can_view_team: Boolean(
          serverReporting.can_view_team ||
            serverReporting.scope === 'team' ||
            serverRealtime.scope === 'team'
        )
      });
      return response;
    } catch (error) {
      if (generation !== stateGeneration) return null;
      if (!quiet && !isBatchApiUnavailable(error)) {
        requestError.value = getErrorMessage(
          error,
          'Unable to load bulk CV parsing settings.'
        );
      }
      return null;
    } finally {
      if (settingsPromise === requestPromise) settingsPromise = null;
    }
  })();
  settingsPromise = requestPromise;
  return requestPromise;
};

const loadAllItems = async (batchId) => {
  const pageSize = 200;
  let page = 1;
  let items = [];
  let response;
  do {
    response = await getBatchItems(batchId, {
      page,
      page_size: pageSize
    });
    const pageItems = getCollection(response);
    items = items.concat(pageItems);
    const total = Number(response?.count);
    const hasAnotherPage = Number.isFinite(total)
      ? items.length < total
      : Boolean(response?.next);
    if (!hasAnotherPage || !pageItems.length) break;
    page += 1;
  } while (true);
  return items;
};

const loadAllActiveBatches = async (params) => {
  let page = 1;
  let rows = [];
  let response;
  do {
    response = await listBatches({ ...params, page });
    const pageRows = getCollection(response);
    rows = rows.concat(pageRows);
    const total = Number(response?.count);
    const hasAnotherPage = Number.isFinite(total)
      ? rows.length < total
      : Boolean(response?.next);
    if (!hasAnotherPage || !pageRows.length) break;
    page += 1;
  } while (true);
  return rows;
};

const refreshActiveBatchOverviews = async () => {
  const generation = stateGeneration;
  const rows = await loadAllActiveBatches({
    include_completed: false,
    status: ACTIVE_BATCH_STATUSES.join(','),
    page_size: 100
  });
  if (generation !== stateGeneration) return [];
  rows.forEach((raw) => {
    const key = raw.reference || raw.batch_reference || raw.id;
    upsertBatch(normalizeBulkCvBatch(raw, itemsByBatch[String(key)] || []));
  });
  return rows;
};

const applyRealtimeBatchUpdate = (payload = {}) => {
  const reference =
    payload.batch_reference || payload.reference || payload.batch?.reference;
  if (!reference) return false;
  const index = batches.value.findIndex(
    (batch) => String(batch.id) === String(reference)
  );
  const current = index >= 0 ? batches.value[index] : null;
  const hasIncomingVersion =
    payload.version !== undefined && payload.version !== null;
  const hasCurrentVersion =
    current?.version !== undefined && current?.version !== null;
  const incomingVersion = Number(payload.version);
  const currentVersion = Number(current?.version);
  if (
    current &&
    hasIncomingVersion &&
    hasCurrentVersion &&
    Number.isFinite(incomingVersion) &&
    Number.isFinite(currentVersion) &&
    incomingVersion < currentVersion
  ) {
    return true;
  }
  if (payload.is_dismissed === true) {
    if (!dismissedBatchIds.value.includes(String(reference))) {
      dismissedBatchIds.value = dismissedBatchIds.value.concat(
        String(reference)
      );
    }
    batches.value = batches.value.filter(
      (batch) => String(batch.id) !== String(reference)
    );
    delete itemsByBatch[String(reference)];
    if (String(selectedBatchId.value) === String(reference)) {
      const preferred =
        batches.value.find((batch) => batch.status === 'action_required') ||
        batches.value.find(
          (batch) => !TERMINAL_BATCH_STATUSES.includes(batch.status)
        ) ||
        batches.value[0];
      setSelectedBatch(preferred?.id || null);
    }
    return true;
  }
  if (index === -1) return false;
  upsertBatch(
    normalizeBulkCvBatch(
      {
        ...current,
        status: payload.status || current.status,
        counts: payload.counts || current.counts,
        version: payload.version ?? current.version,
        updated_at: payload.updated_at || current.updated_at,
        is_dismissed: payload.is_dismissed ?? current.is_dismissed
      },
      itemsByBatch[String(reference)] || []
    )
  );
  return true;
};

const refreshLegacyBatch = async (generation = stateGeneration) => {
  const response = await getLegacyItems();
  if (generation !== stateGeneration) return null;
  const items = getCollection(response).map((item) =>
    normalizeBulkCvItem(item, true)
  );
  if (!items.length) {
    batches.value = batches.value.filter(
      (batch) => String(batch.id) !== LEGACY_BATCH_ID
    );
    delete itemsByBatch[LEGACY_BATCH_ID];
    if (selectedBatchId.value === LEGACY_BATCH_ID) setSelectedBatch(null);
    return null;
  }
  const batch = normalizeBulkCvBatch({}, items, true);
  upsertBatch(batch);
  itemsByBatch[LEGACY_BATCH_ID] = items;
  if (!selectedBatchId.value) setSelectedBatch(LEGACY_BATCH_ID);
  return batch;
};

const refreshBatch = async (batchId, { quiet = false } = {}) => {
  const cleanBatchId = cleanStoredBatchId(batchId);
  if (!cleanBatchId) {
    if (selectedBatchId.value) setSelectedBatch(null);
    return null;
  }
  if (cleanBatchId === LEGACY_BATCH_ID) return refreshLegacyBatch();
  const sequence = ++batchRefreshSequence;
  const generation = stateGeneration;
  batchRefreshSequences.set(cleanBatchId, sequence);
  if (!quiet) {
    visibleBatchRefreshes += 1;
    loadingBatch.value = true;
  }
  try {
    const [batchResponse, itemResponse] = await Promise.all([
      getBatch(cleanBatchId),
      loadAllItems(cleanBatchId)
    ]);
    if (
      generation !== stateGeneration ||
      batchRefreshSequences.get(cleanBatchId) !== sequence
    ) {
      return (
        batches.value.find((batch) => String(batch.id) === cleanBatchId) || null
      );
    }
    const items = itemResponse.map((item) =>
      normalizeBulkCvItem(item, false, cleanBatchId)
    );
    const mergedItems = mergeItemSnapshot(cleanBatchId, items);
    itemsByBatch[cleanBatchId] = mergedItems;
    const batch = normalizeBulkCvBatch(batchResponse, mergedItems);
    upsertBatch(batch);
    requestError.value = '';
    return (
      batches.value.find(
        (candidate) => String(candidate.id) === cleanBatchId
      ) || batch
    );
  } catch (error) {
    if (
      generation !== stateGeneration ||
      batchRefreshSequences.get(cleanBatchId) !== sequence
    ) {
      return (
        batches.value.find((batch) => String(batch.id) === cleanBatchId) || null
      );
    }
    requestError.value = getErrorMessage(
      error,
      'Unable to refresh this batch.'
    );
    throw error;
  } finally {
    if (!quiet && generation === stateGeneration) {
      visibleBatchRefreshes = Math.max(0, visibleBatchRefreshes - 1);
      loadingBatch.value = visibleBatchRefreshes > 0;
    }
  }
};

const refreshBatches = async ({
  quiet = false,
  includeCompleted = false
} = {}) => {
  const requestKind = includeCompleted ? 'history' : 'active';
  if (refreshPromises[requestKind]) return refreshPromises[requestKind];
  let requestPromise;
  requestPromise = (async () => {
    const generation = stateGeneration;
    if (!quiet) loadingBatches.value = true;
    try {
      // Reconcile the selected batch first. If it completed since the previous
      // poll, its terminal snapshot is cached before the active-only list stops
      // returning it.
      const selectedBeforeList = selectedBatch.value;
      const selectedIdBeforeList = selectedBatchId.value;
      let selectedReconciledBeforeList = false;
      let selectedRefreshError = '';
      if (
        !includeCompleted &&
        selectedBatchId.value &&
        String(selectedBatchId.value) !== LEGACY_BATCH_ID &&
        (!selectedBeforeList ||
          !TERMINAL_BATCH_STATUSES.includes(selectedBeforeList.status))
      ) {
        try {
          await refreshBatch(selectedBatchId.value, { quiet: true });
          selectedReconciledBeforeList = true;
        } catch (error) {
          selectedReconciledBeforeList = true;
          if ([403, 404].includes(error?.response?.status)) {
            setSelectedBatch(null);
          } else {
            // One stale or temporarily unavailable selected detail must not
            // prevent the active-list heartbeat from discovering real work.
            selectedRefreshError = getErrorMessage(
              error,
              'The selected batch detail is temporarily unavailable.'
            );
          }
        }
      }

      const listParams = includeCompleted
        ? { include_completed: true, page_size: 100 }
        : {
            include_completed: false,
            status: ACTIVE_BATCH_STATUSES.join(','),
            page_size: 25
          };
      const batchRows = includeCompleted
        ? getCollection(await listBatches(listParams))
        : await loadAllActiveBatches(listParams);
      if (generation !== stateGeneration) return batches.value;
      batchApiMode.value = 'batch';
      const normalized = batchRows.map((batch) => {
        const batchKey = batch.reference || batch.batch_reference || batch.id;
        const inlineItems = Array.isArray(batch.items) ? batch.items : [];
        if (inlineItems.length) {
          itemsByBatch[String(batchKey)] = mergeItemSnapshot(
            batchKey,
            inlineItems.map((item) =>
              normalizeBulkCvItem(item, false, batchKey)
            )
          );
        }
        const incoming = normalizeBulkCvBatch(
          batch,
          itemsByBatch[String(batchKey)] || inlineItems
        );
        const current = batches.value.find(
          (candidate) => String(candidate.id) === String(incoming.id)
        );
        return current && isOlderVersion(incoming, current)
          ? current
          : { ...current, ...incoming };
      });
      const returnedIds = new Set(normalized.map((batch) => String(batch.id)));
      const cachedTerminalBatches = batches.value.filter(
        (batch) =>
          TERMINAL_BATCH_STATUSES.includes(batch.status) &&
          !returnedIds.has(String(batch.id)) &&
          !batch.is_legacy
      );
      const missingActiveBatches = includeCompleted
        ? []
        : batches.value.filter(
            (batch) =>
              !batch.is_legacy &&
              !TERMINAL_BATCH_STATUSES.includes(batch.status) &&
              !returnedIds.has(String(batch.id))
          );
      const reconciledMissing = [];
      if (missingActiveBatches.length) {
        const results = await Promise.allSettled(
          missingActiveBatches.map((batch) => getBatch(batch.id))
        );
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            reconciledMissing.push(
              normalizeBulkCvBatch(
                result.value,
                itemsByBatch[String(missingActiveBatches[index].id)] || []
              )
            );
          } else if (![403, 404].includes(result.reason?.response?.status)) {
            // A transient detail failure must not make active work disappear.
            reconciledMissing.push(missingActiveBatches[index]);
          }
        });
      }
      if (generation !== stateGeneration) return batches.value;
      const reconciledRows = normalized
        .concat(reconciledMissing)
        .concat(cachedTerminalBatches)
        .filter((batch) => !wasDismissedThisSession(batch))
        .filter(
          (batch, index, collection) =>
            collection.findIndex(
              (candidate) => String(candidate.id) === String(batch.id)
            ) === index
        )
        .map((incoming) => {
          const current = batches.value.find(
            (batch) => String(batch.id) === String(incoming.id)
          );
          return current && isOlderVersion(incoming, current)
            ? current
            : incoming;
        });
      batches.value = reconciledRows;

      // A rolling deployment can have both new batches and an older per-user
      // queue. Keep the legacy queue visible until it is explicitly completed.
      try {
        await refreshLegacyBatch(generation);
      } catch (_) {
        // The new API remains usable even when a deployment removes legacy URLs.
      }
      if (generation !== stateGeneration) return batches.value;

      const selectedStillExists = batches.value.some(
        (batch) => String(batch.id) === String(selectedBatchId.value)
      );
      if (!selectedStillExists) {
        const preferred =
          batches.value.find((batch) => batch.status === 'action_required') ||
          batches.value.find(
            (batch) => !TERMINAL_BATCH_STATUSES.includes(batch.status)
          ) ||
          batches.value[0];
        setSelectedBatch(preferred?.id || null);
      }
      const selected = batches.value.find(
        (batch) => String(batch.id) === String(selectedBatchId.value)
      );
      if (
        selectedBatchId.value &&
        selected &&
        String(selected.id) !== LEGACY_BATCH_ID &&
        (includeCompleted ||
          !selectedReconciledBeforeList ||
          String(selected.id) !== String(selectedIdBeforeList))
      ) {
        try {
          await refreshBatch(selectedBatchId.value, { quiet: true });
          selectedRefreshError = '';
        } catch (error) {
          if ([403, 404].includes(error?.response?.status)) {
            setSelectedBatch(null);
          } else {
            selectedRefreshError = getErrorMessage(
              error,
              'The selected batch detail is temporarily unavailable.'
            );
          }
        }
      }
      if (generation !== stateGeneration) return batches.value;
      requestError.value = selectedRefreshError;
      initialized.value = true;
      return batches.value;
    } catch (error) {
      if (generation !== stateGeneration) return batches.value;
      if (isBatchApiUnavailable(error)) {
        batchApiMode.value = 'legacy';
        await refreshLegacyBatch(generation);
        if (generation !== stateGeneration) return batches.value;
        if (
          selectedBatchId.value &&
          !batches.value.some(
            (batch) => String(batch.id) === String(selectedBatchId.value)
          )
        ) {
          setSelectedBatch(batches.value[0]?.id || null);
        }
        initialized.value = true;
        return batches.value;
      }
      requestError.value = getErrorMessage(
        error,
        'Unable to load bulk CV batches.'
      );
      throw error;
    } finally {
      if (!quiet && generation === stateGeneration) {
        loadingBatches.value = false;
      }
      if (refreshPromises[requestKind] === requestPromise) {
        refreshPromises[requestKind] = null;
      }
    }
  })();
  refreshPromises[requestKind] = requestPromise;
  return requestPromise;
};

const selectBatch = async (batchId) => {
  setSelectedBatch(batchId);
  return refreshBatch(batchId);
};

const createAndStartBatch = async ({ job, files, clientFiles }) => {
  creatingBatch.value = true;
  requestError.value = '';
  try {
    const draft = pendingDraft.value;
    if (draft?.batch?.id) {
      job = draft.job;
      files = draft.files;
      clientFiles = draft.clientFiles;
    }

    files = Array.from(files || []);
    clientFiles = Array.from(clientFiles || []);
    if (files.length > BULK_CV_FILE_POLICY.maxFiles) {
      const excessCount = files.length - BULK_CV_FILE_POLICY.maxFiles;
      const limitError = new Error(
        `A bulk CV upload can contain a maximum of ${
          BULK_CV_FILE_POLICY.maxFiles
        } files. Remove ${excessCount} file${
          excessCount === 1 ? '' : 's'
        } and try again.`
      );
      limitError.code = 'max_files_exceeded';
      throw limitError;
    }

    if (!draft?.batch?.id) {
      uploadProgress.batchId = null;
      uploadProgress.completedFiles = 0;
      uploadProgress.totalFiles = files.length;
      uploadProgress.percent = 0;
      uploadProgress.stage = 'prepare';
      uploadProgress.error = '';
    }

    let batch;
    if (draft?.batch?.id) {
      batch = draft.batch;
    } else {
      const batchName = `${
        job?.position || 'Candidate-only CV parsing'
      } – ${new Date().toLocaleString()}`;
      let created;
      try {
        created = await createBatch({
          job_id: job?.id ?? null,
          name: batchName,
          expected_file_count: clientFiles.length,
          // The backend may safely resume/finish this durable workflow if the
          // browser disconnects after the final confirmed upload chunk.
          start_when_uploaded: true,
          manifest: clientFiles.map((file) => ({
            client_file_id: file.client_file_id,
            name: file.name,
            size: file.size_bytes
          }))
        });
        batchApiMode.value = 'batch';
      } catch (error) {
        // New uploads must never bypass enhanced validation. In particular, a
        // business-level 404 such as `invalid_job` is not proof that the API is
        // unavailable and must not fall through to the legacy upload endpoint.
        throw error;
      }

      batch = normalizeBulkCvBatch(created);
      if (!batch.id)
        throw new Error('The batch API did not return a batch ID.');
      pendingDraft.value = {
        batch,
        job,
        files,
        clientFiles,
        nextChunkIndex: 0,
        stage: 'upload'
      };
      uploadProgress.completedFiles = 0;
      uploadProgress.totalFiles = files.length;
      uploadProgress.percent = 0;
      uploadProgress.stage = 'upload';
      uploadProgress.error = '';
      upsertBatch(batch);
      setSelectedBatch(batch.id);
    }

    uploadProgress.batchId = batch.id;
    uploadProgress.totalFiles = files.length;
    uploadProgress.error = '';

    if (pendingDraft.value.stage === 'upload') {
      const chunkSize = BULK_CV_FILE_POLICY.uploadChunkSize;
      const totalChunks = Math.ceil(files.length / chunkSize);
      for (
        let chunkIndex = pendingDraft.value.nextChunkIndex;
        chunkIndex < totalChunks;
        chunkIndex += 1
      ) {
        uploadProgress.stage = 'upload';
        const start = chunkIndex * chunkSize;
        const chunkFiles = files.slice(start, start + chunkSize);
        const chunkMetadata = clientFiles.slice(start, start + chunkSize);
        const uploadResponse = await uploadBatchFiles(
          batch.id,
          chunkFiles,
          chunkMetadata
        );
        if (uploadResponse?.batch) {
          upsertBatch(normalizeBulkCvBatch(uploadResponse.batch));
        }
        const acceptedItems = Array.isArray(uploadResponse?.items)
          ? uploadResponse.items
          : [];
        const rejectedItems = Array.isArray(uploadResponse?.rejected)
          ? uploadResponse.rejected
          : [];
        const confirmedRejectedItems = rejectedItems.filter(
          (item) => item.id || item.item_id
        );
        const confirmedItems = acceptedItems.concat(confirmedRejectedItems);
        if (confirmedItems.length) {
          const currentItems = itemsByBatch[String(batch.id)] || [];
          const returnedIds = new Set(
            confirmedItems.map((item) =>
              String(item.id || item.item_id || item.client_file_id)
            )
          );
          setBatchItems(
            batch.id,
            currentItems
              .filter(
                (item) =>
                  !returnedIds.has(
                    String(item.id || item.client_file_id || item.file_id)
                  )
              )
              .concat(confirmedItems)
          );
        }

        const confirmedClientIds = new Set(
          confirmedItems.map((item) => String(item.client_file_id || ''))
        );
        const unconfirmedFiles = chunkMetadata.filter(
          (item) => !confirmedClientIds.has(String(item.client_file_id))
        );
        if (unconfirmedFiles.length) {
          const firstFailure = rejectedItems.find(
            (item) =>
              !item.id &&
              !item.item_id &&
              unconfirmedFiles.some(
                (file) =>
                  String(file.client_file_id) === String(item.client_file_id)
              )
          );
          const uploadError = new Error(
            firstFailure?.message ||
              `${unconfirmedFiles.length} file${
                unconfirmedFiles.length === 1 ? ' was' : 's were'
              } not confirmed by the server. Use Resume Upload to continue this batch.`
          );
          uploadError.code = firstFailure?.code || 'upload_not_confirmed';
          throw uploadError;
        }
        pendingDraft.value.nextChunkIndex = chunkIndex + 1;
        uploadProgress.completedFiles = Math.min(
          files.length,
          (chunkIndex + 1) * chunkSize
        );
        uploadProgress.percent = Math.round(
          (uploadProgress.completedFiles / files.length) * 100
        );
      }
      pendingDraft.value.stage = 'start';
    }

    uploadProgress.stage = 'start';
    let started;
    try {
      // This remains explicit for immediate feedback. The endpoint is
      // idempotent because the final upload may already have auto-started the
      // durable batch before this request reaches the server.
      started = await startBatch(batch.id);
    } catch (startError) {
      // A timeout or a rolling deployment response can arrive after the server
      // has committed the start. Reconcile before presenting a false failure.
      const authoritative = normalizeBulkCvBatch(await getBatch(batch.id));
      upsertBatch(authoritative);
      if (!STARTED_BATCH_STATUSES.includes(authoritative.status)) {
        throw startError;
      }
      started = authoritative;
    }
    if (started) upsertBatch(normalizeBulkCvBatch(started));
    await refreshBatch(batch.id, { quiet: true });
    const completedBatch = batches.value.find(
      (item) => String(item.id) === String(batch.id)
    );
    pendingDraft.value = null;
    uploadProgress.stage = 'complete';
    uploadProgress.percent = 100;
    return { batch: completedBatch, legacy: false };
  } catch (error) {
    requestError.value = getErrorMessage(
      error,
      'The CV batch could not be started.'
    );
    uploadProgress.error = requestError.value;
    throw error;
  } finally {
    creatingBatch.value = false;
  }
};

const cancelPendingDraft = async () => {
  if (!pendingDraft.value?.batch) return null;
  const batch = pendingDraft.value.batch;
  const response = await cancelBatchRequest(batch.id);
  pendingDraft.value = null;
  uploadProgress.batchId = null;
  uploadProgress.completedFiles = 0;
  uploadProgress.totalFiles = 0;
  uploadProgress.percent = 0;
  uploadProgress.stage = null;
  uploadProgress.error = '';
  await refreshBatches({ quiet: true });
  return response;
};

const upsertReturnedItem = (batchId, response) => {
  const rawItem = response?.item || response?.data || response;
  if (!rawItem?.id && !rawItem?.item_id) return;
  const item = normalizeBulkCvItem(rawItem, false, batchId);
  const current = itemsByBatch[String(batchId)] || [];
  const index = current.findIndex(
    (candidate) => String(candidate.id) === String(item.id)
  );
  if (index === -1) itemsByBatch[String(batchId)] = [item, ...current];
  else {
    const next = current.slice();
    next[index] = item;
    itemsByBatch[String(batchId)] = next;
  }
};

const resolveItem = async (item, action, options = {}) => {
  if (!item?.id || actionLoading[item.id]) return null;
  actionLoading[item.id] = true;
  try {
    if (item.is_legacy) {
      const legacyAction = {
        [BULK_CV_ACTIONS.OVERRIDE]: 'override',
        [BULK_CV_ACTIONS.DUPLICATE]: 'duplicate',
        [BULK_CV_ACTIONS.SKIP]: 'discard'
      }[action];
      if (!legacyAction) {
        throw new Error('This action requires the enhanced batch API.');
      }
      const candidateId = item.parsed_candidate?.id;
      const payload =
        legacyAction === 'discard'
          ? { discard: [{ id: candidateId }] }
          : { save: [{ id: candidateId, action: legacyAction }] };
      const response = await resolveLegacyItem(payload);
      if (response?.code && Number(response.code) !== 0) {
        throw new Error(response.message || 'The decision could not be saved.');
      }
      await refreshLegacyBatch();
      return response;
    }

    const itemBatch = batches.value.find(
      (batch) => String(batch.id) === String(item.batch_id)
    );
    const response = await resolveBatchItem(item.id, {
      action,
      existing_candidate_id: options.existing_candidate_id || undefined,
      // Candidate-only batches deliberately omit a selected job so resolution
      // cannot create an applicant relationship by accident.
      selected_job_id: itemBatch?.job?.id
        ? options.selected_job_id || itemBatch.job.id
        : undefined,
      confirmed: Boolean(options.confirmed ?? options.confirmation),
      version: item.version ?? undefined
    });
    upsertReturnedItem(item.batch_id, response);
    await refreshBatch(item.batch_id, { quiet: true });
    return response;
  } finally {
    actionLoading[item.id] = false;
  }
};

const bulkResolve = async (batchId, selectedItems, action) => {
  const key = `batch-${batchId}`;
  if (actionLoading[key] || !selectedItems.length) return null;
  const conditionKeys = new Set(
    selectedItems.map((item) => item.condition_key)
  );
  if (conditionKeys.size !== 1 || !selectedItems[0]?.condition_key) {
    throw new Error(
      'Bulk decisions can only be applied to matching conditions.'
    );
  }
  if (selectedItems.some((item) => item.scenario === 'multiple_matches')) {
    throw new Error(
      'Resolve multiple-match CVs individually so the correct candidate is explicit.'
    );
  }
  if (selectedItems.some((item) => !item.allowed_actions.includes(action))) {
    throw new Error('This decision is not available for every selected CV.');
  }
  actionLoading[key] = true;
  try {
    if (selectedItems.every((item) => item.is_legacy)) {
      const legacyAction = {
        [BULK_CV_ACTIONS.OVERRIDE]: 'override',
        [BULK_CV_ACTIONS.DUPLICATE]: 'duplicate',
        [BULK_CV_ACTIONS.SKIP]: 'discard'
      }[action];
      if (!legacyAction)
        throw new Error('This action requires the enhanced API.');
      const candidates = selectedItems.map((item) => ({
        id: item.parsed_candidate?.id
      }));
      const payload =
        legacyAction === 'discard'
          ? { discard: candidates }
          : {
              save: candidates.map((candidate) => ({
                ...candidate,
                action: legacyAction
              }))
            };
      const response = await resolveLegacyItem(payload);
      if (response?.code && Number(response.code) !== 0) {
        throw new Error(response.message || 'The bulk decision failed.');
      }
      await refreshLegacyBatch();
      return response;
    }

    const response = await resolveBatchItems(batchId, {
      item_ids: selectedItems.map((item) => item.id),
      action,
      condition_key: selectedItems[0].condition_key,
      confirmed: true
    });
    await refreshBatch(batchId, { quiet: true });
    return response;
  } finally {
    actionLoading[key] = false;
  }
};

const cancel = async (batch) => {
  if (!batch?.id) return null;
  const response = batch.is_legacy
    ? await cancelLegacyBatch()
    : await cancelBatchRequest(batch.id);
  await refreshBatches({ quiet: true });
  return response;
};

const markLegacyComplete = async () => {
  const response = await completeLegacyBatch();
  await refreshBatches({ quiet: true });
  return response;
};

const dismissBatch = async (batch) => {
  if (
    !batch?.id ||
    batch.is_legacy ||
    !TERMINAL_BATCH_STATUSES.includes(batch.status)
  ) {
    return false;
  }

  const batchId = String(batch.id);
  const loadingKey = `dismiss-${batchId}`;
  if (actionLoading[loadingKey]) return false;
  actionLoading[loadingKey] = true;
  try {
    const response = await dismissBatchRequest(batch.id);
    dismissedBatchIds.value = [
      ...dismissedBatchIds.value.filter((id) => id !== batchId),
      batchId
    ];
    batches.value = batches.value.filter(
      (candidate) => String(candidate.id) !== batchId
    );
    delete itemsByBatch[batchId];

    if (String(selectedBatchId.value) === batchId) {
      const preferred =
        batches.value.find(
          (candidate) => candidate.status === 'action_required'
        ) ||
        batches.value.find(
          (candidate) => !TERMINAL_BATCH_STATUSES.includes(candidate.status)
        ) ||
        batches.value[0];
      setSelectedBatch(preferred?.id || null);
    }
    return response;
  } finally {
    actionLoading[loadingKey] = false;
  }
};

const selectedBatch = computed(() =>
  batches.value.find(
    (batch) => String(batch.id) === String(selectedBatchId.value)
  )
);

const resetState = ({ preserveSelection = false } = {}) => {
  stateGeneration += 1;
  batchRefreshSequences.clear();
  settingsPromise = null;
  refreshPromises.active = null;
  refreshPromises.history = null;
  visibleBatchRefreshes = 0;
  batches.value = [];
  dismissedBatchIds.value = [];
  Object.keys(itemsByBatch).forEach((key) => delete itemsByBatch[key]);
  pendingDraft.value = null;
  requestError.value = '';
  initialized.value = false;
  batchApiMode.value = 'unknown';
  loadingBatches.value = false;
  loadingBatch.value = false;
  Object.keys(actionLoading).forEach((key) => delete actionLoading[key]);
  Object.keys(permissions).forEach((key) => delete permissions[key]);
  Object.keys(candidateOnlyPermissions).forEach(
    (key) => delete candidateOnlyPermissions[key]
  );
  Object.assign(realtime, {
    authorized: false,
    websocket_url: '',
    subprotocol: '',
    ticket_expires_in_seconds: 0,
    scope: 'own'
  });
  Object.assign(reporting, { scope: 'own', can_view_team: false });
  uploadProgress.batchId = null;
  uploadProgress.completedFiles = 0;
  uploadProgress.totalFiles = 0;
  uploadProgress.percent = 0;
  uploadProgress.stage = null;
  uploadProgress.error = '';
  Object.assign(policy, {
    allow_duplicate_candidates: true,
    allow_duplicate_applicants_same_job: false,
    automatically_update_candidate: false,
    keep_cv_version_history: true,
    auto_add_existing_candidate: false,
    duplicate_matching_fields: [],
    max_file_size: BULK_CV_FILE_POLICY.maxSizeBytes,
    max_files: BULK_CV_FILE_POLICY.maxFiles,
    supported_extensions: BULK_CV_FILE_POLICY.extensions.slice(),
    parsing_delay_seconds: null
  });
  if (!preserveSelection) setSelectedBatch(null);
  else setSelectedBatch(selectedBatchId.value);
};
const selectedItems = computed(
  () => itemsByBatch[String(selectedBatchId.value)] || []
);
const activeBatches = computed(() =>
  batches.value.filter(
    (batch) => !TERMINAL_BATCH_STATUSES.includes(batch.status)
  )
);

const useBulkCvBatches = () => ({
  batches,
  activeBatches,
  selectedBatch,
  selectedBatchId,
  selectedItems,
  itemsByBatch,
  loadingBatches,
  loadingBatch,
  creatingBatch,
  batchApiMode,
  requestError,
  actionLoading,
  initialized,
  pendingDraft,
  uploadProgress,
  uploadTransferLocked,
  policy,
  permissions,
  candidateOnlyPermissions,
  realtime,
  reporting,
  loadSettings,
  refreshBatches,
  refreshBatch,
  refreshActiveBatchOverviews,
  applyRealtimeBatchUpdate,
  selectBatch,
  createAndStartBatch,
  cancelPendingDraft,
  resolveItem,
  bulkResolve,
  cancel,
  markLegacyComplete,
  dismissBatch,
  resetState,
  setBatchItems,
  normalizeAction,
  getErrorMessage
});

export default useBulkCvBatches;
